/**
 * section：scroll-animation-banner
 * 核心功能：基于滚动的帧动画+元素动效控制
 * 依赖：GSAP + ScrollTrigger + Lenis 平滑滚动
 */
// ==================== 依赖引入 ====================
import gsap from "https://cdn.shopify.com/s/files/1/0637/3297/3671/files/gsap.mjs?v=1775188103";
import { ScrollTrigger } from "https://cdn.shopify.com/s/files/1/0637/3297/3671/files/scrollTrigger.mjs?v=1775188103";
import Lenis from "https://cdn.shopify.com/s/files/1/0637/3297/3671/files/lenis.mjs";

// 注册GSAP插件
gsap.registerPlugin(ScrollTrigger);

// ==================== 配置常量（集中管理，便于调整） ====================
const CONFIG = {
  // 帧动画相关
  FRAME_COUNT: 123, // 总帧数
  FRAME_BASE_PATH: "https://cdn.shopify.com/s/files/1/0637/3297/3671/files/", // 帧图片基础路径
  // 滚动相关
  SCROLL_MULTIPLIER: 4, // 滚动长度 = 视口高度 * 该值
  // 动画参数
  HEADER_MIN_OPACITY: 0.18, // 标题最低透明度
  DROP_DURATION: 0.1, // 标题下沉动画时长（相对比例）
  HOLD_DURATION: 0.6, // 标题保持时长（相对比例）
  RISE_DURATION: 0.2, // 标题上升动画时长（相对比例）
  EASE_NAME: "power2.out", // 默认缓动函数，power0（匀速）/power1~4.in/out/inOut，数值越大缓动效果越剧烈
  // 位置偏移（视口高度比例）
  HEADER_BOTTOM_OFFSET_RATIO: 0.3, // 标题下沉偏移比例
  HEADER_FINAL_OFFSET_RATIO: -0.18, // 标题最终偏移比例
  // 导航透明度控制
  NAV_FADE_OUT_RATIO: 0.05, // 导航淡出进度点
  NAV_FADE_IN_START_RATIO: 0.75, // 导航淡入开始进度点
  NAV_FADE_IN_END_RATIO: 0.8, // 导航淡入结束进度点
};

// ==================== 全局变量（缓存DOM和状态） ====================
let lenis = null; // 平滑滚动实例
let canvasContext = null; // canvas 2D上下文
let frameImages = []; // 预加载的帧图片数组
let currentFrameState = { frame: 0 }; // 当前帧状态
let resizeTimer = null; // 防抖定时器
// let wavesInstance = null; // 保存Waves实例

// ==================== DOM元素缓存 ====================
const DOM = {
  nav: document.querySelector(".shopify-section-group-header-group"),
  heroSection: document.querySelector(".hero-section"),
  heroHeader: document.querySelector(".hero-header"),
  clientLogos: document.querySelector(".client-logos"),
  canvas: document.querySelector(".hero-canvas"),
  heroOverlay: document.querySelector(".hero-overlay"),
  h1Element: document.querySelector(".hero-header h1"),
  h1Video: document.querySelector(".h1-bg-video"),
  heroSubtitle: document.querySelector(".hero-header p"),
};

// ==================== 工具函数 ====================
/**
 * 检查必要DOM元素是否存在
 * @returns {boolean} 是否全部存在
 */
const checkRequiredElements = () => {
  const requiredElements = [
    DOM.heroSection,
    DOM.heroHeader,
    DOM.canvas,
    DOM.heroOverlay,
  ];
  const missingElements = requiredElements.filter((el) => !el);

  if (missingElements.length > 0) {
    console.warn("必要DOM元素缺失,脚本退出:", missingElements);
    return false;
  }
  return true;
};

/**
 * 格式化帧图片路径
 * @param {number} index 帧索引
 * @returns {string} 格式化后的路径
 */
const getFramePath = (index) => {
  const frameNum = index; // 方便处理index + 1;
  return `${CONFIG.FRAME_BASE_PATH}video_frame_${frameNum.toString().padStart(4, "0")}.jpg`;
};

/**
 * 计算数值的区间映射（clamp + 线性映射）
 * @param {number} value 原始值
 * @param {number} inMin 输入最小值
 * @param {number} inMax 输入最大值
 * @param {number} outMin 输出最小值
 * @param {number} outMax 输出最大值
 * @returns {number} 映射后的值
 */
const mapRange = (value, inMin, inMax, outMin, outMax) => {
  const clamped = Math.min(Math.max(value, inMin), inMax);
  return ((clamped - inMin) / (inMax - inMin)) * (outMax - outMin) + outMin;
};

// ==================== 初始化函数 ====================
/**
 * 1. 初始化窗口大小调整监听
 */
const initResizeListener = () => {
  window.addEventListener("resize", () => {
    // 调整Canvas尺寸并重新渲染
    setCanvasSize();
    renderFrame(currentFrameState.frame);

    // 防抖刷新ScrollTrigger
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      ScrollTrigger.refresh();
      renderFrame(currentFrameState.frame);
    }, 120);
  });
};

/**
 * 2. 设置Canvas尺寸（适配设备像素比）
 */
const setCanvasSize = () => {
  if (!DOM.canvas) return;

  const dpr = window.devicePixelRatio || 1;
  const width = document.documentElement.clientWidth;
  const height = document.documentElement.clientHeight;

  // 设置canvas实际像素尺寸（适配高DPI屏幕）
  // 画布尺寸
  DOM.canvas.width = Math.round(width * dpr);
  DOM.canvas.height = Math.round(height * dpr);

  // 设置canvas显示尺寸
  DOM.canvas.style.width = `${width}px`;
  DOM.canvas.style.height = `${height}px`;

  // 重置canvas变换矩阵
  canvasContext?.setTransform(1, 0, 0, 1, 0, 0);
  canvasContext?.scale(dpr, dpr);
};

/**
 * 3. 初始化Lenis平滑滚动，默认配置下 Lenis 的 lagSmoothing: 0.2 + GSAP ticker lagSmoothing(0) 的组合
 */
const initLenis = () => {
  lenis = new Lenis();

  // 同步Lenis滚动与ScrollTrigger
  lenis.on("scroll", ScrollTrigger.update);

  // 绑定GSAP的ticker到Lenis的raf
  gsap.ticker.add((time) => {
    if (typeof lenis.raf === "function") {
      lenis.raf(time * 1000);
    }
  });

  // 停止GSAP的默认requestAnimationFrame，避免双重更新，关闭GSAP ticker的滞后补偿，避免帧序列时间偏差
  gsap.ticker.lagSmoothing(0);
};

/**
 * 4. 预加载帧图片
 * @returns {Promise} 加载完成Promise
 */
const preloadFrames = () => {
  return new Promise((resolve) => {
    let remainingFrames = CONFIG.FRAME_COUNT;
    let failedFrames = 0; // 调试：统计失败数

    // 加载完成回调
    const onFrameLoad = () => {
      remainingFrames--;
      if (remainingFrames <= 0) {
        if (failedFrames > 0) {
          console.warn(`有 ${failedFrames} 帧图片加载失败，请检查路径`);
        }

        console.log("所有帧图片加载完成");
        resolve();
      }
    };

    // 循环加载所有帧
    for (let i = 0; i < CONFIG.FRAME_COUNT; i++) {
      const img = new Image();
      img.onload = onFrameLoad;
      // 加载失败也计数，避免卡死
      img.onerror = () => {
        failedFrames++; // 调试：失败数+1
        console.warn(`帧图片加载失败: ${getFramePath(i)}`);
        onFrameLoad();
      };
      img.src = getFramePath(i);
      frameImages.push(img);
    }
  });
};

/**
 * 5. 初始化Waves(波浪)背景
 */
const initWavesBackground = () => {
  const wavesContainer = document.querySelector(
    ".hero-overlay .waves-container",
  );
  if (wavesContainer) {
    // 初始化时设置容器透明度为0（和overlay初始状态一致）
    wavesContainer.style.opacity = 0;
    wavesContainer.style.transition = "opacity 0.3s linear"; // 和overlay过渡一致

    // 创建Waves实例并保存
    wavesInstance = new Waves(wavesContainer, {
      lineColor: "#c9c9c9b8", // 波浪线颜色
      waveSpeedX: 0.04, // 波浪X轴速度
      waveSpeedY: 0.04, // 波浪Y轴速度
      waveAmpX: 20, // 波浪X轴振幅（调小更柔和）
      waveAmpY: 15, // 波浪Y轴振幅（调小更柔和）
      xGap: 4, // 波浪线条X轴密度
      yGap: 8, // 波浪线条y轴密度
      maxCursorMove: 220, // 鼠标影响范围
      friction: 0.9, // 摩擦力
      tension: 0.015, // 张力
    });
  }
};

/**
 * 6. 渲染指定帧到Canvas
 * @param {number} frameIndex 帧索引
 */
const renderFrame = (frameIndex) => {
  if (!DOM.canvas || !canvasContext) return;

  const canvasWidth = document.documentElement.clientWidth;
  const canvasHeight = document.documentElement.clientHeight;

  // 清空画布
  canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);

  const img = frameImages[frameIndex];
  // 仅当图片有效时绘制
  if (img && img.complete && img.naturalWidth > 0) {
    // 计算宽高比，实现cover效果
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const canvasAspect = canvasWidth / canvasHeight;

    let drawWidth,
      drawHeight,
      drawX = 0,
      drawY = 0;

    if (imgAspect > canvasAspect) {
      drawHeight = canvasHeight;
      drawWidth = drawHeight * imgAspect;
      drawX = (canvasWidth - drawWidth) / 2;
    } else {
      drawWidth = canvasWidth;
      drawHeight = drawWidth / imgAspect;
      drawY = (canvasHeight - drawHeight) / 2;
    }

    // 绘制图片到画布
    canvasContext.drawImage(img, drawX, drawY, drawWidth, drawHeight);
  }
};

/**
 * 7. 初始化滚动触发器和动画
 */
const setupScrollAnimations = () => {
  const {
    SCROLL_MULTIPLIER,
    HEADER_MIN_OPACITY,
    DROP_DURATION,
    HOLD_DURATION,
    RISE_DURATION,
    EASE_NAME,
    HEADER_BOTTOM_OFFSET_RATIO,
    HEADER_FINAL_OFFSET_RATIO,
  } = CONFIG;

  const { innerHeight: viewportHeight } = window;

  // 计算像素偏移值
  const bottomOffset = viewportHeight * HEADER_BOTTOM_OFFSET_RATIO;
  const finalOffset = viewportHeight * HEADER_FINAL_OFFSET_RATIO;

  // 清理已有ScrollTrigger，避免重复
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

  // 创建主时间线
  const tl = gsap.timeline({
    defaults: { ease: EASE_NAME },
    scrollTrigger: {
      trigger: ".hero-section",
      // start: `top - 1px`,
      start: "top top",
      end: `+=${viewportHeight * SCROLL_MULTIPLIER}px`,
      pin: true, // 固定英雄区
      pinSpacing: true, // 保持固定后的空间
      scrub: true, // 平滑跟随滚动

      onUpdate: (self) => {
        // 1. 更新帧动画
        const frameIndex = Math.round(
          // 原始值,输入最小值,输入最大值,输出最小值,输出最大值
          mapRange(self.progress, 0, 1, 0, CONFIG.FRAME_COUNT - 1),
        );
        if (frameIndex !== currentFrameState.frame) {
          currentFrameState.frame = frameIndex;
          renderFrame(frameIndex);
        }

        // 2. 更新导航样式
        updateNavStyles(self.progress);

        // 3. 更新标题样式
        updateHeaderStyles(self.progress);
      },
    },
  });

  // 1. 导航初始淡出
  tl.to(DOM.nav || {}, { opacity: 0, duration: 0.1 }, 0);

  // 2. 标题下沉动画
  tl.to(
    DOM.heroHeader,
    {
      y: bottomOffset,
      opacity: HEADER_MIN_OPACITY,
      duration: DROP_DURATION,
    },
    0,
  );

  // 3. 标题保持阶段（占位动画）
  tl.to({}, { duration: HOLD_DURATION });

  // 4. 标题上升动画
  tl.to(
    DOM.heroHeader,
    {
      y: finalOffset,
      opacity: 1,
      duration: RISE_DURATION,
    },
    DROP_DURATION + HOLD_DURATION,
  );

  // 5. 客户logo动画（上升阶段中后段触发）
  if (DOM.clientLogos) {
    DOM.clientLogos.style.pointerEvents = "none";
    const logosTriggerTime =
      DROP_DURATION + HOLD_DURATION + RISE_DURATION * 0.45;

    // logo容器显示
    tl.to(".client-logos", { opacity: 1, duration: 0.01, pointerEvents: 'auto' }, logosTriggerTime);

    // logo项入场动画
    tl.fromTo(
      ".client-logo",
      { y: 800, scale: 0.92, opacity: 0 },
      {
        y: finalOffset * 0.4,
        scale: 1,
        opacity: 1,
        duration: 0.15, // 持续时间
        ease: "back.out(0.5)",
        stagger: 0.005, // 交错出现
      },
      logosTriggerTime,
    );

    // logo文字淡入
    tl.to(
      ".client-logo p",
      { opacity: 1, y: 0, duration: 0.28, stagger: 0.005 },
      logosTriggerTime + 0.06,
    );
  }

  // 6. 导航最后淡入
  tl.to(
    DOM.nav || {},
    { opacity: 1, y: 0, duration: 0.12 },
    DROP_DURATION + HOLD_DURATION + RISE_DURATION + 0.02,
  );
};

/**
 * 8. 画廊横向移动---横向滚动核心逻辑（修复移动端版）
 */
function initHorizontalGallery() {
  const galleries = document.querySelectorAll(".horiz-scroll-gallery");
  if (!galleries.length) return;

  // 移动端判断
  const isMobile = window.innerWidth < 990;

  galleries.forEach((gallery) => {
    const galleryWrapper = gallery.querySelector(".gallery-wrapper");
    const strip = gallery.querySelector(".horiz-gallery-strip");
    const pageWidthEl = document.querySelector(".page-width");

    if (!galleryWrapper || !strip || !pageWidthEl) return;

    let wrapWidth, scrollLength, marginLeftValue;

    const refreshDimensions = () => {
      // 获取容器总宽度
      wrapWidth = strip.scrollWidth;
      const windowWidth = window.innerWidth;
      const contentWidth = pageWidthEl.offsetWidth;
      
      // 移动端：无侧边距，直接0
      marginLeftValue = windowWidth < 990 ? 0 : (windowWidth - contentWidth) / 2;
      
      // 修复：移动端滚动距离计算公式
      scrollLength = wrapWidth - windowWidth + marginLeftValue;
      // 容错：避免负数导致不滚动
      scrollLength = Math.max(scrollLength, 0);
    };

    // 初始化尺寸
    refreshDimensions();
    ScrollTrigger.addEventListener("refreshInit", refreshDimensions);

    // 统一配置：两端都开启pin，触发点都是 center center
    const scrollTriggerConfig = {
      trigger: gallery,
      pin: galleryWrapper,
      scrub: 1,
      start: "center center",
      end: () => `+=${scrollLength}`,
      invalidateOnRefresh: true,
      fastScrollEnd: true,
    };

    // 执行GSAP动画
    gsap.to(strip, {
      x: () => -scrollLength,
      ease: "none",
      scrollTrigger: scrollTriggerConfig,
    });

    // 修复：监听窗口 resize（横竖屏切换），刷新GSAP
    let resizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 300);
    });
  });
}

/**
 * 更新导航栏样式（透明度+位移）
 * @param {number} progress 滚动进度(0-1)
 */
const updateNavStyles = (progress) => {
  if (!DOM.nav) return;

  // DOM.nav.style.overflow = 'hidden';

  let navOpacity = 0;
  const {
    NAV_FADE_OUT_RATIO,
    NAV_FADE_IN_START_RATIO,
    NAV_FADE_IN_END_RATIO,
    EASE_NAME,
  } = CONFIG;

  // 导航透明度逻辑：淡出 → 隐藏 → 淡入 → 保持显示
  if (progress <= NAV_FADE_OUT_RATIO) {
    const t = progress / NAV_FADE_OUT_RATIO;
    navOpacity = 1 - gsap.parseEase(EASE_NAME)(t);
  } else if (progress >= NAV_FADE_IN_END_RATIO) {
    navOpacity = 1;
  } else if (
    progress > NAV_FADE_IN_START_RATIO &&
    progress < NAV_FADE_IN_END_RATIO
  ) {
    const t =
      (progress - NAV_FADE_IN_START_RATIO) /
      (NAV_FADE_IN_END_RATIO - NAV_FADE_IN_START_RATIO);
    navOpacity = gsap.parseEase(EASE_NAME)(t);
  }

  // 设置导航样式
  gsap.set(DOM.nav, {
    opacity: navOpacity,
    y: (1 - navOpacity) * -10,
  });
};

/**
 * 更新英雄区标题样式（颜色+遮罩）
 * @param {number} progress 滚动进度(0-1)
 */
const updateHeaderStyles = (progress) => {
  // 1. 计算颜色混合进度（0=白色，1=黑色）mapRange:原始值,输入最小值,输入最大值,输出最小值,输出最大值
  const colorMixT = mapRange(progress, 0.6, 0.85, 0, 1);
  // 2. 计算遮罩透明度
  const overlayAlpha = mapRange(progress, 0.6, 0.75, 0, 1);
  // 3. 计算H1背景视频透明度
  const h1BgAlpha = progress >= 0.85 ? 1 : 0;

  // 更新遮罩层
  if (DOM.heroOverlay) {
    DOM.heroOverlay.style.opacity = overlayAlpha;

    // 同步waves-container透明度
    const wavesContainer = DOM.heroOverlay.querySelector(".waves-container");
    if (wavesContainer) {
      wavesContainer.style.opacity = overlayAlpha;
    }
  }

  // 更新H1样式
  if (DOM.h1Element) {
    const colorValue = Math.round(255 * (1 - colorMixT));
    DOM.h1Element.style.color = `rgb(${colorValue},${colorValue},${colorValue})`;

    // 更新H1背景视频
    if (DOM.h1Video) {
      DOM.h1Element.style.backgroundColor = `rgba(255, 255, 255, ${h1BgAlpha})`;
      DOM.h1Element.style.mixBlendMode = "screen";
      DOM.h1Video.style.opacity = h1BgAlpha;
    }
  }

  // 更新副标题样式
  if (DOM.heroSubtitle) {
    const colorValue = Math.round(255 * (1 - colorMixT));
    DOM.heroSubtitle.style.color = `rgb(${colorValue},${colorValue},${colorValue})`;
  }
};

// ==================== 主入口函数 ====================
// 初始化 --- DOM就绪 → 检查元素 → 获取Canvas上下文 → 初始化流程
const init = async () => {
  // 等待DOM加载完成
  await new Promise((resolve) => {
    if (document.readyState === "complete") resolve();
    else document.addEventListener("DOMContentLoaded", resolve);
  });

  // 检查必要元素
  if (!checkRequiredElements()) return;

  // 获取Canvas上下文
  canvasContext = DOM.canvas.getContext("2d");
  if (!canvasContext) {
    console.error("无法获取Canvas 2D上下文");
    return;
  }

  // 初始化流程
  initResizeListener(); // 1. 先绑定resize监听（优先）
  setCanvasSize(); // 2. 再设置Canvas尺寸（依赖resize监听）
  initLenis(); // 3. 初始化平滑滚动（无前置依赖）
  await preloadFrames(); // 4. 预加载帧图片（异步，耗时操作）
  initWavesBackground(); // 5. 初始化波浪背景（依赖Canvas尺寸）
  renderFrame(0); // 6. 初始帧渲染（依赖预加载+Canvas尺寸）
  setupScrollAnimations(); // 7. 最后绑定滚动动画（依赖所有前置）
  initHorizontalGallery(); // 8. 画廊横向移动---横向滚动核心逻辑
};
// 启动初始化
init().catch((err) => {
  console.error("初始化失败:", err);4e44
});

// ==================== 清理函数 ====================
// 页面卸载时清理资源，避免内存泄漏
window.addEventListener("beforeunload", () => {
  lenis?.destroy();
  // wavesInstance?.destroy(); // 清理波浪实例
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
  gsap.ticker.removeAll();
  clearTimeout(resizeTimer);
});
